<script>
import { mapGetters } from 'vuex';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  canShowCloudchatSupportWidget,
  CLOUDCHAT_SUPPORT_SLOT_ATTRIBUTE,
} from 'dashboard/helper/cloudchatSupportHelper';

const SDK_SCRIPT_ID = 'cloudchat-support-sdk';
const SDK_VERSION = '20260820';
const READY_EVENT = 'cloudchat:ready';
const ON_START_CONVERSATION_EVENT = 'cloudchat:on-start-conversation';
const LEFT_VAR = '--cloudchat-support-left';
const BUBBLE_LEFT_VAR = '--cloudchat-support-bubble-left';
const BUBBLE_BOTTOM_VAR = '--cloudchat-support-bubble-bottom';
const BUBBLE_SIZE_VAR = '--cloudchat-support-bubble-size';
const HOLDER_BOTTOM_VAR = '--cloudchat-support-holder-bottom';
const DOCKED_CLASS = 'has-cloudchat-support-slot';
const GLYPH_COLOR_VAR = '--cloudchat-support-glyph-color';
const BUBBLE_SELECTOR = '.woot-widget-bubble:not(.woot--close)';
const HOLDER_GAP = 8;

export default {
  name: 'CloudchatSupportWidget',
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentAccount: 'getCurrentAccount',
      accountId: 'getCurrentAccountId',
      isNewFeatureFlagEnabled: 'isNewFeatureFlagEnabled',
      uiSettings: 'getUISettings',
      conversationId: 'getConversationId',
    }),
    // Single source of truth for who sees the support widget. Mirrors the gate
    // previously held in Sidebar.vue (canShowCloudchatSupportWidget).
    canShowWidget() {
      return canShowCloudchatSupportWidget({
        accountId: this.accountId,
        email: this.currentUser?.email,
        isFeatureFlagEnabled: this.isNewFeatureFlagEnabled,
      });
    },
    // List column is hidden only in expanded layout with a conversation open
    // (mirrors ConversationView.showConversationList). Uses store getters, not
    // route params — the conversation route param name is inconsistent.
    isConversationListHidden() {
      const { CONDENSED } = wootConstants.LAYOUT_TYPES;
      const displayType =
        this.uiSettings?.conversation_display_type || CONDENSED;
      const isExpanded = displayType !== CONDENSED;
      return isExpanded && !!this.conversationId;
    },
  },
  watch: {
    // Gate can flip after mount (account/flag/user resolve async). Inject once
    // on the first `true`; injectOnce is idempotent.
    canShowWidget(canShow) {
      if (canShow) {
        this.injectOnce();
        this.$nextTick(this.measureSupportSlot);
        return;
      }
      this.undockFromSupportSlot();
    },
    // Snap the bubble between the two positions only when the boolean flips.
    isConversationListHidden(hidden) {
      this.applyHolderOffset(hidden);
    },
    'currentUser.cognito_access_token'() {
      if (this.canShowWidget) this.applyAuthToken();
    },
  },
  mounted() {
    const token = window.chatwootConfig?.cloudchatSupportToken;
    const baseUrl = window.chatwootConfig?.cloudchatSupportBaseUrl;
    if (!token || !baseUrl) return;
    this.$_token = token;
    this.$_baseUrl = baseUrl;
    // The `cloudchat:ready` listener must be live before injection so identity
    // is applied as soon as the SDK loads. Also wires the legacy
    // CLOUDCHAT_SUPPORT_ACTIVATE path (OptionsMenu "support" item), which is
    // gated separately (chatwootInboxToken) and must keep working even when the
    // eager-bubble gate (canShowWidget) is false.
    this.registerListeners();
    // Set the initial bubble position before the SDK paints.
    this.applyHolderOffset(this.isConversationListHidden);
    window.addEventListener('resize', this.scheduleSupportSlotMeasure);
    this.$nextTick(this.measureSupportSlot);
    if (this.canShowWidget && window.$cloudchat?.setUser) {
      // The SDK persists on `window` across a component remount (this component
      // can be torn down and recreated while the SDK stays initialized — e.g.
      // the dashboard shell re-renders). `cloudchat:ready` won't refire, so
      // identify synchronously. Gate on canShowWidget so a remount as a
      // NON-eligible user never re-identifies into the prior user's widget.
      this.applyCurrentUser();
    }
    // Initial injection. The watcher above covers a gate that flips later.
    if (this.canShowWidget) this.injectOnce();
  },
  beforeDestroy() {
    this.$emitter.off(BUS_EVENTS.CLOUDCHAT_SUPPORT_ACTIVATE, this.activate);
    if (this.$_cloudchatReadyHandler) {
      window.removeEventListener(READY_EVENT, this.$_cloudchatReadyHandler);
      this.$_cloudchatReadyHandler = null;
    }
    if (this.$_cloudchatStartConversationHandler) {
      window.removeEventListener(
        ON_START_CONVERSATION_EVENT,
        this.$_cloudchatStartConversationHandler
      );
      this.$_cloudchatStartConversationHandler = null;
    }
    this.$emitter.off(
      BUS_EVENTS.CLOUDCHAT_SUPPORT_SLOT_CHANGED,
      this.scheduleSupportSlotMeasure
    );
    window.removeEventListener('resize', this.scheduleSupportSlotMeasure);
    if (this.$_slotMeasureFrame) {
      cancelAnimationFrame(this.$_slotMeasureFrame);
      this.$_slotMeasureFrame = null;
    }
    // Don't leak the global CSS var onto <html> after the component is gone
    // (e.g. when the dashboard shell unmounts).
    document.documentElement.style.removeProperty(LEFT_VAR);
    this.undockFromSupportSlot();
  },
  methods: {
    injectOnce() {
      if (!this.$_token || !this.$_baseUrl) return;
      // Idempotent: never inject twice.
      if (window.$cloudchat || document.getElementById(SDK_SCRIPT_ID)) return;
      // Settings must be set BEFORE sdk.js evaluates. The native bubble (FAB)
      // is shown so the unread badge and message-preview popups appear
      // passively — restoring notifications. Set hideMessageBubble: false
      // EXPLICITLY (not by omission) to defeat a stale value left on the global
      // by an older boot (rolling deploy / cached tab) — otherwise the bubble
      // could stay hidden for users who did not hard-reload.
      window.cloudchatSettings = {
        ...(window.cloudchatSettings || {}),
        hideMessageBubble: false,
        position: 'left',
      };
      this.injectSdk(this.$_token, this.$_baseUrl);
    },
    injectSdk(token, baseUrl) {
      const d = document;
      const t = 'script';
      const g = d.createElement(t);
      const s = d.getElementsByTagName(t)[0];
      g.id = SDK_SCRIPT_ID;
      g.src = `${baseUrl}/packs/js/sdk.js?v=${SDK_VERSION}`;
      g.defer = true;
      g.async = true;
      if (s && s.parentNode) {
        s.parentNode.insertBefore(g, s);
      } else {
        (d.head || d.documentElement).appendChild(g);
      }
      g.onload = () => {
        if (!window.cloudchatSDK?.run) return;
        window.cloudchatSDK.run({
          websiteToken: token,
          baseUrl: baseUrl,
        });
        this.applyAuthToken();
      };
    },
    // Two deterministic positions, consumed by the CSS var in <style>.
    // visible list → flush to its left edge (clears the 64px rail);
    // hidden list  → over the rail (covers the user icon, clears the composer).
    applyHolderOffset(hidden) {
      document.documentElement.style.setProperty(
        LEFT_VAR,
        hidden ? '0px' : '64px'
      );
    },
    scheduleSupportSlotMeasure() {
      if (this.$_slotMeasureFrame) return;
      this.$_slotMeasureFrame = requestAnimationFrame(() => {
        this.$_slotMeasureFrame = null;
        this.measureSupportSlot();
      });
    },
    measureSupportSlot() {
      const slot = document.querySelector(
        `[${CLOUDCHAT_SUPPORT_SLOT_ATTRIBUTE}]`
      );
      if (!slot) {
        this.undockFromSupportSlot();
        return false;
      }
      const rect = slot.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        this.undockFromSupportSlot();
        return false;
      }
      const bottom = window.innerHeight - rect.bottom;
      const { style, classList } = document.documentElement;
      style.setProperty(BUBBLE_LEFT_VAR, `${Math.round(rect.left)}px`);
      style.setProperty(BUBBLE_BOTTOM_VAR, `${Math.round(bottom)}px`);
      style.setProperty(BUBBLE_SIZE_VAR, `${Math.round(rect.width)}px`);
      style.setProperty(
        HOLDER_BOTTOM_VAR,
        `${Math.round(bottom + rect.height + HOLDER_GAP)}px`
      );
      classList.add(DOCKED_CLASS);
      this.syncLauncherGlyphColor();
      return true;
    },
    syncLauncherGlyphColor() {
      const bubble = document.querySelector(BUBBLE_SELECTOR);
      if (!bubble) return;
      const color =
        bubble.style.backgroundColor ||
        window.getComputedStyle(bubble).backgroundColor;
      if (!color || color === 'rgba(0, 0, 0, 0)') return;
      document.documentElement.style.setProperty(GLYPH_COLOR_VAR, color);
    },
    undockFromSupportSlot() {
      const { style, classList } = document.documentElement;
      classList.remove(DOCKED_CLASS);
      [
        BUBBLE_LEFT_VAR,
        BUBBLE_BOTTOM_VAR,
        BUBBLE_SIZE_VAR,
        HOLDER_BOTTOM_VAR,
        GLYPH_COLOR_VAR,
      ].forEach(name => style.removeProperty(name));
    },
    applyAuthToken() {
      const token = this.currentUser?.cognito_access_token;
      if (!token || !window.$cloudchat?.setAuthToken) return;
      window.$cloudchat.setAuthToken(token);
    },
    applyCurrentUser() {
      this.applyAuthToken();
      const email = this.currentUser?.email || '';
      if (!email || !window.$cloudchat?.setUser) return;
      // The support widget can be hmac_mandatory, so identity must be verified with an
      // identifier_hash. `cloudchat_support_identifier_hash` is computed server-side
      // from the support widget's own hmac_token (_user.json.jbuilder), so it always
      // matches what the widget backend validates regardless of CHATWOOT_INBOX_HMAC_KEY.
      window.$cloudchat.setUser(email, {
        email,
        name: this.currentUser?.name || '',
        phone_number: this.currentUser?.phone_number || '',
        identifier_hash:
          this.currentUser?.cloudchat_support_identifier_hash || undefined,
      });
    },
    // Explicit open request from the legacy OptionsMenu "support" item
    // (BUS_EVENTS.CLOUDCHAT_SUPPORT_ACTIVATE). This path is gated upstream by
    // chatwootInboxToken, independent of canShowWidget, so it injects on demand
    // regardless of the eager-bubble gate. injectOnce is idempotent and ungated.
    activate() {
      this.$_pendingOpen = true;
      if (window.$cloudchat?.toggle) {
        if (!window.$cloudchat.isOpen) window.$cloudchat.toggle();
        this.$_pendingOpen = false;
        return;
      }
      this.injectOnce();
    },
    registerListeners() {
      this.$emitter.on(BUS_EVENTS.CLOUDCHAT_SUPPORT_ACTIVATE, this.activate);
      this.$emitter.on(
        BUS_EVENTS.CLOUDCHAT_SUPPORT_SLOT_CHANGED,
        this.scheduleSupportSlotMeasure
      );
      this.$_cloudchatReadyHandler = () => {
        this.applyCurrentUser();
        this.measureSupportSlot();
        this.syncLauncherGlyphColor();
        // Open once if an activation was requested before the SDK was ready.
        if (
          this.$_pendingOpen &&
          window.$cloudchat?.toggle &&
          !window.$cloudchat.isOpen
        ) {
          window.$cloudchat.toggle();
        }
        this.$_pendingOpen = false;
      };
      this.$_cloudchatStartConversationHandler = () => {
        if (!window.$cloudchat?.setCustomAttributes) return;
        const attributes = this.buildCustomAttributes();
        if (!attributes) return;
        window.$cloudchat.setCustomAttributes(attributes);
      };
      window.addEventListener(READY_EVENT, this.$_cloudchatReadyHandler);
      window.addEventListener(
        ON_START_CONVERSATION_EVENT,
        this.$_cloudchatStartConversationHandler
      );
    },
    buildCustomAttributes() {
      const user = this.currentUser;
      if (!user?.id) return null;
      const account = this.currentAccount || {};

      const payload = {
        user_id: user.id,
        user_email: user.email,
        user_name: user.name,
        user_display_name: user.display_name,
        user_available_name: user.available_name,
        user_avatar_url: user.avatar_url,
        user_role: user.role,
        user_provider: user.provider,
        user_confirmed: user.confirmed,
        user_type: user.type,
        account_id: account.id,
        account_name: account.name,
        account_status: account.status,
        account_role: account.role,
      };

      const isScalar = value =>
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean';

      const mergeBag = (bag, prefix) => {
        if (!bag || typeof bag !== 'object') return;
        Object.keys(bag).forEach(key => {
          const value = bag[key];
          if (isScalar(value)) payload[`${prefix}${key}`] = value;
        });
      };

      mergeBag(user.custom_attributes, 'user_attr_');
      mergeBag(user.account_user_custom_attributes, 'account_user_attr_');
      mergeBag(account.custom_attributes, 'account_attr_');

      return Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ''
        )
      );
    },
  },
  render() {
    return null;
  },
};
</script>

<style lang="scss">
// Offset the cloudchat support widget so it clears important UI. Two states are
// driven by `--cloudchat-support-left`, set in JS (applyHolderOffset):
//   64px → flush to the conversation-list column's left edge (clears the
//          64px PrimarySidebar rail);
//   0px  → over the rail, when the list column is hidden (expanded layout
//          with a conversation open), so the bubble never covers the composer.
//
// CRITICAL: the visible FAB and the opened panel are SEPARATE elements with
// SEPARATE left rules in sdk.js. Both must be overridden, or only the panel
// moves while the bubble stays pinned:
//   - `.woot-widget-bubble.woot-elements--left { left: 20px }` (sdk.js:122-123,
//     NO media query — the FAB the user actually sees)
//   - `.woot-widget-holder.woot-elements--left { left: 20px }` (sdk.js:146-148,
//     inside @media min-width:667px — the iframe panel; also carries
//     `has-unread-view`, the unread-preview popup)
// `!important` needed because sdk.js injects its styles at the same specificity
// later in source order. Scope to ≥667px (the dashboard is desktop); below that
// the SDK's own `left: 20px` default applies untouched. `transition` so the
// bubble glides between the two states instead of teleporting on layout flip.
@media only screen and (min-width: 667px) {
  .woot-widget-bubble.woot-elements--left,
  .woot-widget-holder.woot-elements--left {
    left: var(--cloudchat-support-left, 64px) !important;
    transition: left 0.2s ease;
  }
}

html.has-cloudchat-support-slot {
  @media only screen and (min-width: 667px) {
    .woot-widget-bubble.woot-elements--left:not(.woot--hide) {
      left: var(--cloudchat-support-bubble-left, 12px) !important;
      bottom: var(--cloudchat-support-bubble-bottom, 64px) !important;
      width: var(--cloudchat-support-bubble-size, 40px) !important;
      height: var(--cloudchat-support-bubble-size, 40px) !important;
      border-radius: 8px !important;
      box-shadow: none !important;
      z-index: 20 !important;
    }

    .woot-widget-bubble.woot-elements--left:not(.woot--close):not(
        .woot--hide
      )::before {
      content: '?';
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-bottom: 1px;
      font-family: inherit;
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
      color: var(--cloudchat-support-glyph-color, #f76b15);
      pointer-events: none;
    }

    .woot-widget-bubble.woot-elements--left svg {
      width: 20px !important;
      height: 20px !important;
      margin: 10px !important;
    }

    .woot-widget-bubble.woot-elements--left.unread-notification::after {
      width: 10px;
      height: 10px;
      top: -2px;
      right: -2px;
    }

    .woot-widget-bubble.woot-elements--left.woot--close::before {
      height: 14px;
      left: 19px;
      top: 13px;
    }

    .woot-widget-holder.woot-elements--left:not(.woot--hide) {
      bottom: var(--cloudchat-support-holder-bottom, 112px) !important;
    }
  }
}
</style>
